import { getAvailableLanguages, setLanguage, t } from '../../i18n';

const SETTINGS_STORAGE_KEY = 'mmo.settings';
export type RendererMode = '2d' | 'world2d' | '3d-perspective' | '3d-orthographic';

interface BootstrapSettings {
  language: string;
  renderer: RendererMode;
}

let bootstrapSettings: BootstrapSettings | null = null;
let bootstrapSettingsScope: string | null = null;

export interface BootstrapDomRefs {
  stage: HTMLDivElement;
  hud: HTMLSpanElement | null;
  overlay: HTMLDivElement;
  unpauseButton: HTMLButtonElement;
  logoffButton: HTMLButtonElement;
  keyBindingsButton: HTMLButtonElement | null;
  inventoryOverlay: HTMLDivElement;
  inventoryGrid: HTMLDivElement;
  inventoryClose: HTMLButtonElement;
  codexOverlay: HTMLDivElement;
  codexClose: HTMLButtonElement;
  debugOverlay: HTMLDivElement;
  debugClose: HTMLButtonElement;
  itemBarSlots: HTMLDivElement;
  skillBarSlots: HTMLDivElement;
  statusBarRoot: HTMLDivElement | null;
  statusEffectsRoot: HTMLDivElement | null;
  languageSelect: HTMLSelectElement | null;
  rendererSelect: HTMLSelectElement | null;
  rendererRow: HTMLDivElement | null;
}

function getSettingsStorageKey(): string {
  return bootstrapSettingsScope
    ? `${SETTINGS_STORAGE_KEY}.${bootstrapSettingsScope}`
    : SETTINGS_STORAGE_KEY;
}

function loadSettings(): BootstrapSettings {
  try {
    const raw = localStorage.getItem(getSettingsStorageKey());
    if (!raw) return { language: 'en', renderer: '3d-perspective' };
    const parsed = JSON.parse(raw) as { language?: string; renderer?: string };
    const renderer =
      parsed.renderer === '2d'
      || parsed.renderer === 'world2d'
      || parsed.renderer === '3d-perspective'
      || parsed.renderer === '3d-orthographic'
        ? parsed.renderer
        : parsed.renderer === '3d'
          ? '3d-perspective'
          : '3d-perspective';
    return {
      language: parsed.language ?? 'en',
      renderer,
    };
  } catch {
    return { language: 'en', renderer: '3d-perspective' };
  }
}

function saveSettings(settings: BootstrapSettings): void {
  localStorage.setItem(getSettingsStorageKey(), JSON.stringify(settings));
}

function getCachedSettings(): BootstrapSettings {
  if (!bootstrapSettings) {
    bootstrapSettings = loadSettings();
  }
  return bootstrapSettings;
}

function updateCachedSettings(patch: Partial<BootstrapSettings>): BootstrapSettings {
  const next = { ...getCachedSettings(), ...patch };
  bootstrapSettings = next;
  saveSettings(next);
  return next;
}

function applyStaticI18n(): void {
  document.title = t('app.title');

  document.querySelectorAll<HTMLElement>('[data-i18n]').forEach((el) => {
    const key = el.dataset.i18n;
    if (!key) return;
    el.textContent = t(key);
  });

  document.querySelectorAll<HTMLElement>('[data-i18n-attr]').forEach((el) => {
    const spec = el.dataset.i18nAttr;
    if (!spec) return;
    const pairs = spec.split(';').map((entry) => entry.trim()).filter(Boolean);
    for (const pair of pairs) {
      const [attr, key] = pair.split(':').map((part) => part.trim());
      if (!attr || !key) continue;
      el.setAttribute(attr, t(key));
    }
  });
}

export function setupLocalization(languageSelect: HTMLSelectElement | null): void {
  const settings = getCachedSettings();
  setLanguage(settings.language);
  applyStaticI18n();

  if (!languageSelect) {
    return;
  }

  const languages = getAvailableLanguages();
  languageSelect.innerHTML = '';
  languages.forEach(({ code, label }) => {
    const option = document.createElement('option');
    option.value = code;
    option.textContent = label;
    languageSelect.appendChild(option);
  });
  languageSelect.value = settings.language;
  languageSelect.addEventListener('change', () => {
    updateCachedSettings({ language: languageSelect.value });
  });
}

export function setBootstrapSettingsScope(scope: string | null): void {
  const normalizedScope = scope && scope.trim().length > 0 ? scope.trim() : null;
  if (bootstrapSettingsScope === normalizedScope) {
    return;
  }
  bootstrapSettingsScope = normalizedScope;
  bootstrapSettings = null;
}

export function setupRendererSelection(rendererSelect: HTMLSelectElement | null): void {
  const settings = getCachedSettings();
  if (!rendererSelect) {
    return;
  }

  rendererSelect.innerHTML = '';
  const options: Array<{ value: RendererMode; label: string }> = [
    { value: '2d', label: t('settings.renderers.2d') },
    { value: 'world2d', label: '2D (GPU)' },
    { value: '3d-perspective', label: '3D - Perspective' },
    { value: '3d-orthographic', label: '3D - Orthographic' },
  ];
  for (const entry of options) {
    const option = document.createElement('option');
    option.value = entry.value;
    option.textContent = entry.label;
    rendererSelect.appendChild(option);
  }
  rendererSelect.value = settings.renderer;
  rendererSelect.addEventListener('change', () => {
    const nextRenderer: RendererMode =
      rendererSelect.value === '2d'
      || rendererSelect.value === 'world2d'
      || rendererSelect.value === '3d-orthographic'
        ? rendererSelect.value
        : '3d-perspective';
    updateCachedSettings({ renderer: nextRenderer });
  });
}

export function setRendererSelectionVisibility(
  rendererRow: HTMLDivElement | null,
  rendererSelect: HTMLSelectElement | null,
  visible: boolean,
): void {
  if (rendererRow) {
    rendererRow.hidden = !visible;
  }
  if (rendererSelect) {
    rendererSelect.disabled = !visible;
  }
}

export function getBootstrapSettings(): BootstrapSettings {
  return { ...getCachedSettings() };
}

export function persistRendererSetting(renderer: RendererMode): void {
  updateCachedSettings({ renderer });
}

export function queryBootstrapDom(): BootstrapDomRefs {
  const stage = document.querySelector<HTMLDivElement>('#pixi-stage');
  const hud = document.querySelector<HTMLSpanElement>('#tick-status');
  const overlay = document.querySelector<HTMLDivElement>('#pause-overlay');
  const unpauseButton = document.querySelector<HTMLButtonElement>('#pause-unpause');
  const logoffButton = document.querySelector<HTMLButtonElement>('#pause-logoff');
  const keyBindingsButton = document.querySelector<HTMLButtonElement>('#pause-keybindings');
  const inventoryOverlay = document.querySelector<HTMLDivElement>('#inventory-overlay');
  const inventoryGrid = document.querySelector<HTMLDivElement>('#inventory-grid');
  const inventoryClose = document.querySelector<HTMLButtonElement>('#inventory-close');
  const codexOverlay = document.querySelector<HTMLDivElement>('#codex-overlay');
  const codexClose = document.querySelector<HTMLButtonElement>('#codex-close');
  const debugOverlay = document.querySelector<HTMLDivElement>('#debug-overlay');
  const debugClose = document.querySelector<HTMLButtonElement>('#debug-close');
  const itemBarSlots = document.querySelector<HTMLDivElement>('#item-bar-slots');
  const skillBarSlots = document.querySelector<HTMLDivElement>('#skill-bar-slots');
  const statusBarRoot = document.querySelector<HTMLDivElement>('#status-bar-container');
  const statusEffectsRoot = document.querySelector<HTMLDivElement>('#status-effects-container');
  const languageSelect = document.querySelector<HTMLSelectElement>('#settings-language');
  const rendererSelect = document.querySelector<HTMLSelectElement>('#settings-renderer');
  const rendererRow = document.querySelector<HTMLDivElement>('#settings-renderer-row');

  if (
    !stage
    || !overlay
    || !unpauseButton
    || !logoffButton
    || !inventoryOverlay
    || !inventoryGrid
    || !inventoryClose
    || !codexOverlay
    || !codexClose
    || !debugOverlay
    || !debugClose
    || !itemBarSlots
  ) {
    throw new Error('Missing required DOM elements');
  }

  if (!skillBarSlots) {
    throw new Error('Missing bar slots');
  }

  document.getElementById('game-root')?.addEventListener('contextmenu', (event) => {
    event.preventDefault();
  });

  return {
    stage,
    hud,
    overlay,
    unpauseButton,
    logoffButton,
    keyBindingsButton,
    inventoryOverlay,
    inventoryGrid,
    inventoryClose,
    codexOverlay,
    codexClose,
    debugOverlay,
    debugClose,
    itemBarSlots,
    skillBarSlots,
    statusBarRoot,
    statusEffectsRoot,
    languageSelect,
    rendererSelect,
    rendererRow,
  };
}
